import React, { useCallback } from "react";

import Select from "antd/es/select";
import { useIntl } from "react-intl";

import { StandardRange } from "@mapmycustomers/shared/types/range/DateRange";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import SelectField from "@app/component/input/SelectField";

import useMonthOptions from "../utils/useMonthOptions";

const CLEAR_OPTION_VALUE = "clear";

interface Props {
  allowClear?: boolean;
  className?: string;
  disabled?: boolean;
  fromDate?: Date;
  label?: React.ReactNode;
  onChange?: (value?: StandardRange) => void;
  value?: StandardRange;
}

const MonthSelect: React.FC<Props> = ({
  allowClear,
  className,
  disabled,
  fromDate,
  label,
  onChange,
  value,
}) => {
  const intl = useIntl();
  const months = useMonthOptions(intl, fromDate);

  const handleChange = useCallback(
    (value: StandardRange | typeof CLEAR_OPTION_VALUE) => {
      onChange?.(value === CLEAR_OPTION_VALUE ? undefined : (value as StandardRange));
    },
    [onChange]
  );

  return (
    <SelectField<StandardRange | typeof CLEAR_OPTION_VALUE>
      className={className}
      disabled={disabled}
      dropdownMatchSelectWidth={false}
      label={
        label ??
        intl.formatMessage({
          id: "dateRangeSelect.month",
          defaultMessage: "Select month",
          description: "Month field label in range selector component",
        })
      }
      onChange={handleChange}
      optionFilterProp="children"
      showSearch
      value={value}
    >
      {allowClear && isDefined(value) && (
        <Select.Option key={CLEAR_OPTION_VALUE} value={CLEAR_OPTION_VALUE}>
          {intl.formatMessage({
            id: "dateRangeSelect.month.options.clear",
            defaultMessage: "Clear",
            description: "Clear option in the Month selector component",
          })}
        </Select.Option>
      )}
      {months.map((monthGroup) => {
        return (
          <Select.OptGroup key={monthGroup.key} label={monthGroup.label}>
            {monthGroup.options.map((range) => (
              <Select.Option key={range.value} value={range.value}>
                {range.label}
              </Select.Option>
            ))}
          </Select.OptGroup>
        );
      })}
    </SelectField>
  );
};

export default MonthSelect;
